import get from 'lodash/get';
import {useState} from 'react';

import 'react-credit-cards/es/styles-compiled.css';
import Cards from 'react-credit-cards';
import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconShield from '@mui/icons-material/Shield';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useUser from '../../hooks/useUser';
import {
  useGetCardsQuery,
  useCreateCustomerMutation,
  useRemoveCardMutation,
  useUpdateCardMutation
} from '../../redux/services/paymentApi';
import StaxPaymentForm from '../payments/StaxPaymentForm';

// https://stripe.com/docs/stripe-js/react
const stripePromise = loadStripe(config().stripePublicKey);

const getIssuer = card => {
  return card.brand.toLowerCase().replace(' ', '');
};

const handleSubmit = ({elements, stripe, setStripeError, updateCard, onClose}) => (
  async ({promoCode}) => {
    if (!stripe || !elements) {
      return setStripeError({message: 'Stripe is not ready yet.'});
    }
    const cardElement = elements.getElement("card");
    const {token, error} = await stripe.createToken(cardElement);
    if (error) return setStripeError({message: 'Error - we are unable to connect your credit card. Please make sure all information is accurate. If you continue to have difficulty, please contact us at 202-681-1474 and our Customer Success team will be of assistance!'});
    const response = await updateCard({stripe_token: token.id});
    if (response.error) {
      return setStripeError({message: 'Error updating information'});
    }
    onClose();
  }
);


// Stripe test numbers:
// 4242 4242 4242 4242 Visa
// 5555 5555 5555 4444 MasterCard
// 3782 8224 6310 005 American Express
// 6011 1111 1111 1117 Discover
const CardEditForm = props => {
  const {user} = props;
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [updateCard] = useUpdateCardMutation();
  return (
    <>
      <DialogContent>
        <Stack spacing={2}>
          {
            stripeError
            ? <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {stripeError.message}
            </Alert>
            : <></>
          }
          <CardElement
            onReady={() => setReady(true)}
          />
          <Stack direction='row' spacing={0.5} alignItems='center' style={{paddingTop: '2em'}}>
            <IconShield color='primary' />
            <Typography variant='body' color='grey' style={{fontSize: '0.8rem'}}>
              MovementX does not store your credit card information. Payments processed securely by Stripe.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant='outlined'>
          Close
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit({
            elements,
            stripe,
            setStripeError,
            updateCard,
            onClose: props.onClose,
          })}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};

const EditCreditCard = props => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const user = useUser();
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCard, updateResponse] = useUpdateCardMutation();
  const [removeCard, deleteResponse] = useRemoveCardMutation();
  const patient = user?.patient || null;
  const response = useGetCardsQuery({}, {skip: false});
  const cards = get(response, 'data.data', []);
  const hasDefaultMethod = (cards || []).find(c => c.is_default);
  const noPaymentInfo = (response?.error?.data?.errors || []).includes('User does not have a customer_id');
  const error = updateResponse.error || deleteResponse.error;
  console.log('deleteResponse', deleteResponse);
  return (
    <Stack spacing={1} padding={1.5} alignItems='center'>
      <Typography variant='h2' padding={2}>Payment Method</Typography>
      <Box>
        <Button
          disabled={!patient}
          variant='contained'
          onClick={() => setDialogIsOpen(true)}
        >
          <AddIcon style={{marginRight: '0.125rem'}} />
          New Payment Method
        </Button>
      </Box>
      {error && <Alert severity='error'>
        {error?.data?.errors?.[0] || 'An error occurred updating your payment information.'}
      </Alert>
      }
      {
        patient && cards
        ? 
        <Grid container maxWidth='700px'>
            {patient?.stax_customer_id ? cards.map((card, i) => 
              <Grid item xs={12} md={6}>
                <Box 
                  style={{
                    background:'#fff', 
                    padding: '1rem', 
                    margin: '0.5rem', 
                    borderRadius: '8px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    width: '320px',
                  }}
                >
                  <Cards
                    expiry={(card.exp || '').replace(/[\s]/g, '')}
                    preview
                    number={'**** **** **** ' + card.digits}
                    issuer={getIssuer(card)}
                    name={card?.person_name || ((patient?.user?.first_name || '') + ' ' + (patient?.user?.last_name || ''))}
                  />
                  <Stack direction='row' paddingTop={1} justifyContent='space-between' alignItems='center'>
                    {
                      deleteResponse.isLoading
                      ? <CircularProgress size={24} />
                      : <Button 
                        disabled={card.is_default || cards.length === 1}
                        color='error'
                        onClick={async e => removeCard({id: card.id})}
                        loading={deleteResponse.isLoading}
                      >
                        Delete
                      </Button>
                    }
                    {(hasDefaultMethod ? card.is_default : i === 0)
                      ? <Box>
                          <Chip color='success' label='Default' />
                        </Box>
                      : (
                        updateResponse.isLoading
                        ? <CircularProgress size={24} />
                        : <Button 
                          disabled={card.is_default} 
                          color='primary'
                          loading={updateResponse.isLoading}
                          onClick={async e => updateCard({id: card.id, is_default: true})}
                        >
                          Make Default
                        </Button>
                      )
                    }
                  </Stack>
                </Box>
              </Grid>
            ) : null}
        </Grid>
        : null
      }
      <Dialog
        open={dialogIsOpen}
        fullWidth={true}
        maxWidth='xs'
        onClose={() => setDialogIsOpen(false)}
      >
        <Stack padding={1.5} spacing={1}>
          <Typography variant='h2' align='center'>
            New Payment Method
          </Typography>
          <Divider/>
          {
            patient?.payment_platform === 'stax'
            ? <Stack direction='row' justifyContent='center'>
                <StaxPaymentForm 
                  isEditing={true}
                  user={patient ? {...patient?.user, patient, user: patient.user} : null}
                  onSubmit={() => {
                    setDialogIsOpen(false);
                    response.refetch();
                  }}
                />
            </Stack>
            : 
            <Stack direction='row' justifyContent='center'>
              <StaxPaymentForm 
                isEditing={false}
                user={patient ? {...patient?.user, patient, user: patient.user} : null}
                showOnboardingFields={false}
                onSubmit={async formData => {
                  const {customer_id} = formData;
                  const {error} = await createCustomer({customer_id, is_stax: true});
                  if (!error) {
                    setDialogIsOpen(false);
                    response.refetch();
                  }
                }}
              />
            </Stack>
            // <Elements
            //   stripe={stripePromise}
            //   options={{clientSecret: props.stripeClientSecret}}
            // >
            //   <CardEditForm
            //     user={patient}
            //     onClose={() => setDialogIsOpen(false)}
            //   />
            // </Elements>
          }
        </Stack>
      </Dialog>
    </Stack>
  );
};

export default EditCreditCard;
